import type { PlayerId } from "../core/ids.ts";
import { normalizePlanar } from "../core/math.ts";
import { createInputIntent, type InputIntent } from "./intents.ts";
import { KeyboardInputAdapter } from "./keyboard.ts";
import { PointerInputAdapter } from "./pointer.ts";
import { TouchInputAdapter } from "./touch.ts";

export class PlayerInputAdapter {
  readonly #keyboard: KeyboardInputAdapter;
  readonly #pointer: PointerInputAdapter;
  readonly #touch = new TouchInputAdapter();

  constructor(target: Window, initialLookYaw = 0) {
    this.#keyboard = new KeyboardInputAdapter(target);
    this.#pointer = new PointerInputAdapter(initialLookYaw);
  }

  attach(): void {
    this.#keyboard.attach();
  }

  detach(): void {
    this.#keyboard.detach();
  }

  diagnostics(): Readonly<{
    keyboardAttached: boolean;
    pressedKeyboardCodes: readonly string[];
  }> {
    const keyboard = this.#keyboard.diagnostics();
    return {
      keyboardAttached: keyboard.attached,
      pressedKeyboardCodes: keyboard.pressedCodes,
    };
  }

  rotateLookByPixels(movementX: number): void {
    this.#pointer.rotateByPixels(movementX);
  }

  resetLookYaw(yaw: number): void {
    this.#pointer.resetYaw(yaw);
  }

  lookYaw(): number {
    return this.#pointer.sampleYaw();
  }

  setTouchMovement(moveX: number, moveZ: number): void {
    this.#touch.setMovement(moveX, moveZ);
  }

  setTouchJumpHeld(held: boolean): void {
    this.#touch.setJumpHeld(held);
  }

  setTouchDescendHeld(held: boolean): void {
    this.#touch.setDescendHeld(held);
  }

  setTouchSprintHeld(held: boolean): void {
    this.#touch.setSprintHeld(held);
  }

  pressTouchInteract(): void {
    this.#touch.pressInteract();
  }

  pressTouchCycleSeat(): void {
    this.#touch.pressCycleSeat();
  }

  pressTouchToggleFlight(): void {
    this.#touch.pressToggleFlight();
  }

  resetTouch(): void {
    this.#touch.reset();
  }

  sampleIntent(playerId: PlayerId, tick: number): InputIntent {
    const keyboard = this.#keyboard.sample();
    const touch = this.#touch.sample();
    const movement = normalizePlanar(
      keyboard.moveX + touch.moveX,
      keyboard.moveZ + touch.moveZ,
    );
    return createInputIntent(playerId, tick, {
      moveX: movement.x,
      moveZ: movement.z,
      lookYaw: this.#pointer.sampleYaw(),
      jumpPressed: keyboard.jumpPressed || touch.jumpPressed,
      ascendHeld: keyboard.jumpHeld || touch.ascendHeld,
      descendHeld: keyboard.descendHeld || touch.descendHeld,
      sprintHeld: keyboard.sprintHeld || touch.sprintHeld,
      interactPressed: keyboard.interactPressed || touch.interactPressed,
      cycleSeatPressed: keyboard.cycleSeatPressed || touch.cycleSeatPressed,
      toggleFlightPressed:
        keyboard.toggleFlightPressed || touch.toggleFlightPressed,
      brakeHeld: keyboard.brakeHeld,
      reverseHeld: keyboard.reverseHeld,
      handbrakeHeld: keyboard.handbrakeHeld,
      hornHeld: keyboard.hornHeld,
      lookBehindHeld: keyboard.lookBehindHeld,
      toggleHeadlightsPressed: keyboard.toggleHeadlightsPressed,
      toggleBeaconPressed: keyboard.toggleBeaconPressed,
      toggleSirenPressed: keyboard.toggleSirenPressed,
      toggleLeftIndicatorPressed: keyboard.toggleLeftIndicatorPressed,
      toggleRightIndicatorPressed: keyboard.toggleRightIndicatorPressed,
      recoverVehiclePressed: keyboard.recoverVehiclePressed,
    });
  }
}
